import axios from "axios";
import React from "react";
import { useLocation } from "react-router-dom";
import Headerpage from "src/components/header";
import Footerpage from "src/components/footer";
import { decodeEmailQueryParam } from "src/utils/queryParams";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

const invitationApi = axios.create({
  baseURL: "/api/",
  withCredentials: true,
});

const InvitationPasswordPage: React.FC = () => {
  const query = new URLSearchParams(useLocation().search);
  const token = query.get("token") || "";
  const email = decodeEmailQueryParam(query.get("email")) || "";
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [code, setCode] = React.useState("");
  const [otpSent, setOtpSent] = React.useState(false);
  const [otpExpired, setOtpExpired] = React.useState(false);
  const [otpSecondsRemaining, setOtpSecondsRemaining] = React.useState(0);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!otpSent || otpExpired || otpSecondsRemaining <= 0) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setOtpSecondsRemaining((seconds) => {
        if (seconds <= 1) {
          setOtpExpired(true);
          return 0;
        }
        return seconds - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [otpSent, otpExpired, otpSecondsRemaining]);

  const requestOtp = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setError("");
    if (password.length < 8 || password !== confirmPassword) {
      setError("Enter a valid password and make sure both passwords match.");
      return;
    }
    setLoading(true);
    try {
      await invitationApi.post("invitation/request-otp", {
        token,
        email,
      }, { withCredentials: true });
      setOtpSent(true);
      setOtpExpired(false);
      setOtpSecondsRemaining(2 * 60);
      setCode("");
    } catch (err: any) {
      setError(err?.response?.data?.message || "Unable to send the verification code.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      await invitationApi.post("invitation/verify-otp", {
        token,
        email,
        code,
      }, { withCredentials: true });

      await invitationApi.post("invitation/set-password", {
        token,
        email,
        password,
        confirmPassword,
      }, { withCredentials: true });
      localStorage.removeItem("invitationToken");
      localStorage.removeItem("invitationEmail");
      window.location.href = "/";
    } catch (err: any) {
      const isExpired = err?.response?.status === 410;
      setOtpExpired(isExpired);
      if (isExpired) {
        setOtpSecondsRemaining(0);
      }
      setError(err?.response?.data?.message || "Invalid verification code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Headerpage />
      <div className="container-inner">
        <div className="content-leader" style={{
          alignItems: "center",
          backgroundImage: `-webkit-gradient(linear, left top, left bottom, from(rgba(62, 124, 177, 0.5)), to(rgba(62, 124, 177, 0.5))),url(${require("src/assets/gritp.jpg")})`,
          display: "flex",
          justifyContent: "center",
          minHeight: "calc(100vh - 153px)",
          width: "100%",
        }}>
          <div className="welcome-back welcome-sign-outer" style={{ maxWidth: 620, padding: "36px 48px" }}>
            <h1 className="sign-in-head" style={{ marginTop: 0 }}>Accept your invitation</h1>
            <p style={{ color: "#08045f", fontSize: 18 }}>The administrator invited {email}.</p>
            {!otpSent ? (
              <form onSubmit={requestOtp} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <input value={email} disabled={true} type="email" style={{ padding: 12 }} />
                <div style={{ position: "relative" }}>
                  <input value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Create a Password" type={showPassword ? "text" : "password"} required={true} style={{ padding: 12, paddingRight: 44, width: "100%", boxSizing: "border-box" }} />
                  <button type="button" aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", border: 0, background: "transparent", cursor: "pointer", padding: 4 }}>
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </button>
                </div>
                <div style={{ position: "relative" }}>
                  <input value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder="Confirm password" type={showConfirmPassword ? "text" : "password"} required={true} style={{ padding: 12, paddingRight: 44, width: "100%", boxSizing: "border-box" }} />
                  <button type="button" aria-label={showConfirmPassword ? "Hide password" : "Show password"} onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", border: 0, background: "transparent", cursor: "pointer", padding: 4 }}>
                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                  </button>
                </div>
                <button className="login_button" disabled={loading} type="submit">{loading ? "Sending..." : "Send verification code"}</button>
              </form>
            ) : (
              <form onSubmit={verifyOtp} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <p style={{ color: "#08045f", fontSize: 17 }}>A verification code was sent to your invited email.</p>
                <div style={{ position: "relative" }}>
                  <input value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Create a Password" type={showPassword ? "text" : "password"} required={true} style={{ padding: 12, paddingRight: 44, width: "100%", boxSizing: "border-box" }} />
                  <button type="button" aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", border: 0, background: "transparent", cursor: "pointer", padding: 4 }}>
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </button>
                </div>
                <div style={{ position: "relative" }}>
                  <input value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder="Confirm password" type={showConfirmPassword ? "text" : "password"} required={true} style={{ padding: 12, paddingRight: 44, width: "100%", boxSizing: "border-box" }} />
                  <button type="button" aria-label={showConfirmPassword ? "Hide password" : "Show password"} onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", border: 0, background: "transparent", cursor: "pointer", padding: 4 }}>
                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                  </button>
                </div>
                <input value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Enter 6-digit verification code" inputMode="numeric" maxLength={6} pattern="[0-9]{6}" required={true} style={{ padding: 12 }} />
                <button className="login_button" disabled={loading || code.length !== 6} type="submit">{loading ? "Verifying..." : "Verify email"}</button>
                {otpExpired && <button disabled={loading} type="button" onClick={() => requestOtp()} style={{ background: "none", border: 0, color: "#08045f", cursor: "pointer" }}>Resend code</button>}
              </form>
            )}
            {error && <p style={{ color: "red", fontWeight: 700 }}>{error}</p>}
          </div>
        </div>
      </div>
      <Footerpage />
    </>
  );
};

export default InvitationPasswordPage;



// WEBPACK FOOTER //
// ./src/pages/home/signIn-page/InvitationPasswordPage.tsx